import type { ISaaSModule } from '@/types/saas';
import type { ITPAModule } from '@/types/tpa';

// Lazy-loaded SaaS module factories
export const saasRegistry: Record<string, () => Promise<{ createModule: () => ISaaSModule }>> = {
  zohocrm: () => import('@/modules/saas/zoho/index'),
  hubspotcrm: () => import('@/modules/saas/hubspot/index'),
  pipedrive: () => import('@/modules/saas/pipedrive/index'),
  bitrix24: () => import('@/modules/saas/bitrix24/index'),
  salesforce: () => import('@/modules/saas/salesforce/index'),
  freshworkscrm: () => import('@/modules/saas/freshworks/index'),
  freshdesk: () => import('@/modules/saas/freshdesk/index'),
  zendesksell: () => import('@/modules/saas/zendesk-sell/index'),
  zendesksupport: () => import('@/modules/saas/zendesk-support/index'),
  shopify: () => import('@/modules/saas/shopify/index'),
  dynamics: () => import('@/modules/saas/dynamics/index'),
  activecampaign: () => import('@/modules/saas/activecampaign/index'),
  monday: () => import('@/modules/saas/monday/index'),
  airtable: () => import('@/modules/saas/airtable/index'),
  leadsquared: () => import('@/modules/saas/leadsquared/index'),
  odoo: () => import('@/modules/saas/odoo/index'),
  zohodesk: () => import('@/modules/saas/zoho-desk/index'),
  zohobooks: () => import('@/modules/saas/zoho-books/index'),
  zohobigin: () => import('@/modules/saas/zoho-bigin/index'),
  zohocampaigns: () => import('@/modules/saas/zoho-campaigns/index'),
  zohorecruit: () => import('@/modules/saas/zoho-recruit/index'),
  googlesheets: () => import('@/modules/saas/google-sheets/index'),
  slack: () => import('@/modules/saas/slack/index'),
  woocommerce: () => import('@/modules/saas/woocommerce/index'),
  spreadsheet: () => import('@/modules/saas/spreadsheet/index'),
  uaweb: () => import('@/modules/saas/ua-web/index'),
};

// Lazy-loaded TPA module factories
export const tpaRegistry: Record<string, () => Promise<{ createModule: () => ITPAModule }>> = {
  twilio: () => import('@/modules/tpa/twilio/index'),
  twiliocall: () => import('@/modules/tpa/twiliocall/index'),
  messagebird: () => import('@/modules/tpa/messagebird/index'),
  whatsapp: () => import('@/modules/tpa/whatsapp/index'),
  whatsappweb: () => import('@/modules/tpa/whatsappweb/index'),
  whatsappweb2: () => import('@/modules/tpa/whatsappweb/index'),
  vonage: () => import('@/modules/tpa/vonage/index'),
  ringcentral: () => import('@/modules/tpa/ringcentral/index'),
  calendly: () => import('@/modules/tpa/calendly/index'),
  clickatell: () => import('@/modules/tpa/clickatell/index'),
  exotel: () => import('@/modules/tpa/exotel/index'),
  karix: () => import('@/modules/tpa/karix/index'),
  kaleyra: () => import('@/modules/tpa/kaleyra/index'),
  infobip: () => import('@/modules/tpa/infobip/index'),
  textlocal: () => import('@/modules/tpa/textlocal/index'),
  msg91: () => import('@/modules/tpa/msg91/index'),
  gupshup: () => import('@/modules/tpa/gupshup/index'),
  plivo: () => import('@/modules/tpa/plivo/index'),
  sinch: () => import('@/modules/tpa/sinch/index'),
  telnyx: () => import('@/modules/tpa/telnyx/index'),
  textmagic: () => import('@/modules/tpa/textmagic/index'),
  clicksend: () => import('@/modules/tpa/clicksend/index'),
  bulksms: () => import('@/modules/tpa/bulksms/index'),
  burstsms: () => import('@/modules/tpa/burstsms/index'),
  kudosity: () => import('@/modules/tpa/burstsms/index'),
  routemobile: () => import('@/modules/tpa/routemobile/index'),
  routemobilewa: () => import('@/modules/tpa/routemobilewa/index'),
  pinnacle: () => import('@/modules/tpa/pinnacle/index'),
  rivet: () => import('@/modules/tpa/rivet/index'),
  telebu: () => import('@/modules/tpa/telebu/index'),
  mtalkz: () => import('@/modules/tpa/mtalkz/index'),
  grapevine: () => import('@/modules/tpa/grapevine/index'),
  acuityscheduling: () => import('@/modules/tpa/acuity-scheduling/index'),
  stripe: () => import('@/modules/tpa/stripe/index'),
  googledrive: () => import('@/modules/tpa/google-drive/index'),
  recurringtasks: () => import('@/modules/tpa/recurring-tasks/index'),
  intercom: () => import('@/modules/tpa/intercom/index'),
  anysms: () => import('@/modules/tpa/anysms/index'),
  lookup: () => import('@/modules/tpa/lookup/index'),
  aiactions: () => import('@/modules/tpa/aiactions/index'),
};

// External SDKs that need script injection for certain SaaS platforms
export const SAAS_EXTERNAL_SDKS: Record<string, string[]> = {
  zohocrm: ['https://live.zwidgets.com/js-sdk/1.5/ZohoEmbededAppSDK.min.js'],
  zohobigin: ['https://js.zohocdn.com/biginsdk/dist/js/bigin-widget-sdk.js'],
  // zohodesk SDK is loaded eagerly by sdk-init.ts → zoho-desk/init.ts (calls extension.onload)
  bitrix24: ['https://api.bitrix24.com/api/v1/'],
  zendesksupport: ['https://static.zdassets.com/zendesk_app_framework_sdk/2.0/zaf_sdk.min.js'],
  zendesksell: ['https://static.zdassets.com/zendesk_app_framework_sdk/2.0/zaf_sdk.min.js'],
};

/**
 * Parse an extension name like "twilioforzohocrm" into TPA and SaaS codes
 */
export function parseExtensionName(extensionName: string): {
  tpaCode: string;
  saasCode: string;
} | null {
  // Non-greedy first group so `pinnacleforsalesforce` → pinnacle + salesforce
  // (greedy would lock onto the last "for" in `salesforce`).
  const match = extensionName.match(/^(\w+?)for(\w+)$/);
  if (!match) return null;
  return {
    tpaCode: match[1],
    saasCode: match[2],
  };
}

/**
 * Load an external SDK by injecting a <script> tag
 */
export function loadExternalSDK(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    const existing = document.querySelector(`script[src="${url}"]`);
    if (existing) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load SDK: ${url}`));
    document.head.appendChild(script);
  });
}
